import { createProgram, FLAT_COLOR_FRAGMENT_SHADER } from "./shaderProgram";
import { bindUnitQuad, bindInstanceAttributes, UNIT_QUAD_VERTEX_COUNT } from "./unitQuad";

/**
 * Base of all geometry renderers: a unit square drawn once per instance.
 * Instance data stays in JS (`instances`), so after a lost WebGL context
 * `init` rebuilds the program and buffers from it. Pan and zoom only change
 * uniforms passed to `draw`; the instance buffer is uploaded only by
 * `setInstances` (whole) and `updateInstances` (a tail in place).
 * Initialised with a Canvas 2D context instead (no WebGL2, or the context
 * was not restored: glLayer), it paints the same instances on the CPU.
 *
 * @param {object} params
 * @param {string} params.vertexShader - reads the corner at location 0, instance attributes from 1
 * @param {string} [params.fragmentShader] - a flat colour from the vertex shader by default
 * @param {string[]} params.uniformNames
 * @param {number[]} params.attributeComponents - float components of each instance attribute
 */
export const createInstancedRenderer = ({
  vertexShader,
  fragmentShader = FLAT_COLOR_FRAGMENT_SHADER,
  uniformNames,
  attributeComponents,
}) => {
  const floatsPerInstance = attributeComponents.reduce((sum, count) => sum + count, 0);
  let gl = null;
  let canvasContext = null;
  let program = null;
  let uniforms = null;
  let vertexArray = null;
  let quadBuffer = null;
  let instanceBuffer = null;
  let instances = new Float32Array(0);

  const upload = () => {
    gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, instances, gl.DYNAMIC_DRAW);
  };

  return {
    floatsPerInstance,

    /**
     * Creates GPU resources; called at start and after the context is restored.
     * A Canvas 2D context needs no resources, `draw` paints with it.
     *
     * @param {WebGL2RenderingContext|CanvasRenderingContext2D} context
     */
    init: (context) => {
      if (context instanceof CanvasRenderingContext2D) {
        gl = null;
        canvasContext = context;
        return;
      }

      gl = context;
      canvasContext = null;
      ({ program, uniforms } = createProgram(gl, vertexShader, fragmentShader, uniformNames));
      vertexArray = gl.createVertexArray();
      gl.bindVertexArray(vertexArray);
      quadBuffer = bindUnitQuad(gl);
      instanceBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
      bindInstanceAttributes(gl, attributeComponents);
      gl.bindVertexArray(null);
      upload();
    },

    /**
     * Frees the buffers of a renderer that is no longer drawn (a removed
     * indicator); the shared program stays (shaderProgram).
     */
    release: () => {
      if (gl && !gl.isContextLost()) {
        gl.deleteVertexArray(vertexArray);
        gl.deleteBuffer(quadBuffer);
        gl.deleteBuffer(instanceBuffer);
      }

      gl = null;
      canvasContext = null;
      instances = new Float32Array(0);
    },

    /** @param {Float32Array} data - `floatsPerInstance` floats per instance */
    setInstances: (data) => {
      instances = data;

      if (gl) upload();
    },

    /**
     * Rewrites instances from `firstInstance` on in place (a realtime tick
     * changes only the last bar); the instance count stays the same.
     *
     * @param {number} firstInstance
     * @param {Float32Array} data - whole instances that fit before the end
     */
    updateInstances: (firstInstance, data) => {
      const firstFloat = firstInstance * floatsPerInstance;

      instances.set(data, firstFloat);

      if (!gl) return;

      gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, firstFloat * Float32Array.BYTES_PER_ELEMENT, data);
    },

    /**
     * @param {(gl: WebGL2RenderingContext, uniforms: object) => void} setUniforms
     * @param {(context: CanvasRenderingContext2D, instances: Float32Array) => void} paint -
     *   the same drawing on the Canvas 2D fallback
     */
    draw: (setUniforms, paint) => {
      const instanceCount = instances.length / floatsPerInstance;

      if (instanceCount === 0) return;

      if (canvasContext) {
        paint(canvasContext, instances);
        return;
      }

      if (!gl) return;

      gl.useProgram(program);
      setUniforms(gl, uniforms);
      gl.bindVertexArray(vertexArray);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, UNIT_QUAD_VERTEX_COUNT, instanceCount);
      gl.bindVertexArray(null);
    },
  };
};
